import { useState, useRef, useEffect } from 'react';

export type SelectProps = {
    title?: string;
    containerClassName?: string;
    className?: string;
    options?: { label: string; value: string }[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    id?: string;
    disabled?: boolean;
};

export default function Select({
    title,
    containerClassName = '',
    className = '',
    options = [],
    value,
    onChange,
    placeholder = 'Select an option',
    required = false,
    id,
    disabled = false,
}: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value || '');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find((o) => o.value === selectedValue);

    useEffect(() => {
        if (value !== undefined) {
            setSelectedValue(value);
        }
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
        setSelectedValue(optionValue);
        setIsOpen(false);
        if (onChange) {
            onChange(optionValue);
        }
    };

    return (
        <div className={containerClassName}>
            {title && (
                <label htmlFor={id} className="block mb-1 text-secondary font-semibold">
                    {title}
                    {required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}
            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    id={id}
                    disabled={disabled}
                    className={`w-full appearance-none px-4 py-3 border border-[#D2D2D2] rounded-lg focus:ring-2 focus:ring-[#151515] focus:border-[#151515] outline-none transition-colors bg-gray-50 focus:bg-white text-[14px] text-left ${
                        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    } ${className}`}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    <span className={selectedOption ? 'text-[#151515]' : 'text-[#52525B]'}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                </button>
                <svg
                    className={`h-4 w-4 text-[#52525B] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>

                {/* Custom Dropdown */}
                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-[#D2D2D2] rounded-lg shadow-lg max-h-60 overflow-auto">
                        {options.map((option) => (
                            <div
                                key={option.value}
                                className={`px-4 py-3 cursor-pointer transition-colors text-[14px] hover:bg-gray-50 ${
                                    selectedValue === option.value
                                        ? 'bg-gray-50 text-[#151515] font-semibold'
                                        : 'text-[#151515]'
                                }`}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                        {options.length === 0 && (
                            <div className="px-4 py-3 text-[14px] text-[#52525B] text-center">No options available</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
